import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { differenceInDays, format } from 'date-fns';
import type { TemplateResult } from 'lit';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import type { Einzelberatung } from '../entities/einzelberatung.js';
import { Institution } from '../entities/institution.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import { Product } from '../entities/products.js';
import { Setting, SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import type { Workshop } from '../entities/workshop.js';
import { ParticipationState } from '../enums/enums.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { buildNewsletterOptinLink, buildRegistrationsHTML } from '../mails/mail-helpers.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { mongo } from '../server/mongo.js';
import { isDefined } from '../util.js';
import { getNotificationType, sendWhatsappWithEmailFallback } from './_helper.js';
import { getProductByID } from '../api/product.js';
import { getInstitutionByID } from '../api/institution.js';

defineMailTemplate(MailTemplateType.ABMELDUNG, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Abmeldebestätigung',
    content: `
          <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>wir haben Sie soeben von folgender Veranstaltung abgemeldet:</p>
<p>{{changed-registrations}}</p>
<p>Die Abmeldung bezieht sich nur auf die aufgef&uuml;hrte Veranstaltung. Falls uns ein Fehler unterlaufen sein sollte, bitten wir Sie, sich mit uns <a href="mailto:contact@anmeldung.uni-frankfurt.de">per Mail </a>in Verbindung zu setzen.</p>
<p>Eine Auflistung Ihrer Veranstaltungsanmeldungen f&uuml;r das aktuelle Semester finden Sie unter folgendem Link: {{login-link}}</p>
<p>Wir freuen uns, wenn wir Sie in Zukunft in einer unserer Veranstaltungen begr&uuml;&szlig;en d&uuml;rfen und empfehlen Ihnen hierf&uuml;r unser Vortrags- und Workshop-Programm auf <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de.</a></p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>You have been deregistered from the following event:</p>
<p>{{changed-registrations}}</p>
<p>The deregistration refers only to the listed event. If we have made a mistake, please contact us by e-mail.</p>
<p>A list of event registrations for the current semester can be found at the followig link: {{login-link}}</p>
<p>We look forward to welcoming you to one of our events in the future and recommend our lecture and workshop program at <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de.</a> for this purpose.</p>
<p>Best regards</p>
      `,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.ABMELDUNG),
  attachments: [],
  displayName: 'Abmeldung',
  editorHint: () => institutionMailHint(MailTemplateType.ABMELDUNG),
});

function assertExists<T>(d: Maybe<T>, name?: string): asserts d is T {
  if (d === null)
    throw new Error(
      name ? `expected value to be defined, but it is null (${name})` : 'expected value to be defined, but it is null',
    );
  if (d === undefined)
    throw new Error(
      name
        ? `expected value to be defined, but it is undefined (${name})`
        : 'expected value to be defined, but it is undefined',
    );
}

// TODO: remove property useOldSlots and handle that case more gracefully
export async function notifyAbmeldung(data: { participationID: string; throughAdmin?: boolean; useOldSlots?: any[] }) {
  // TODO: implement whatsapp

  const { participationID } = data;
  const participationCol = mongo().collection<Participation>(Participation._collectionName);
  const participation = await participationCol.raw.findOne<Participation>({ _id: participationID });

  assertExists(participation, 'participation');

  const { productID, userID } = participation;
  const productSelectionSet = {
    _id: 1,
    topic: 1,
    presenceType: 1,
    institutionID: 1,
    language: 1,
    name: 1,
    hasPrice: 1,
    slots: { _id: 1, start: 1, end: 1, berater: 1 },
    appointments: { name: 1, start: 1, end: 1, location: 1 },
    settings: selectionSet(() => Setting, 2),
  } as const;

  const product = await getProductByID(productID)(productSelectionSet);
  assertExists(product, 'product');
  const userCol = mongo().collection<User>(User._collectionName);
  const user = await userCol.raw.findOne<User>({ _id: userID });
  if (!user) throw new Error('user not found');
  assertExists(user, 'user');

  if (data.useOldSlots) (product as Einzelberatung).slots = data.useOldSlots;

  // set product for later data retrieval
  participation.product = product as Product;

  const institutionID = product.institutionID;
  const institution = await getInstitutionByID(institutionID)({
    ...Institution.allFields,
    colorDefinitions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });
  assertExists(institution, 'institution');

  const start = (product as Workshop).appointments?.[0]?.start;
  const daysTillEvent = start ? differenceInDays(start, new Date()).toFixed(0) : undefined;
  let dates = (product as Workshop).appointments
    ?.map(a => (a.start ? format(a.start, 'dd.MM.yyyy') : null))
    .filter(isDefined)
    .join(', ');
  if (!dates) dates = start ? format(start, 'dd.MM.yyyy') : '---';

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const templateType = data.throughAdmin ? MailTemplateType.ABMELDUNG_DURCH_EINRICHTUNG : MailTemplateType.ABMELDUNG;

  // set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  const mailTemplate = (await getMailTemplateForInstitution(
    institutionID,
    templateType,
  )(InstitutionMailTemplate.allFields)) as InstitutionMailTemplate;

  const sendEmail = async () => {
    const mailVars = {
      [MailTemplateVars.EVENT_TITLE]: product.name,
      [MailTemplateVars.PRODUCT_NAME]: product.name,
      [MailTemplateVars.EVENT_DATES]: dates ?? '',
      [MailTemplateVars.EVENT_LEAD_DAYS]: daysTillEvent,
      [MailTemplateVars.TITLE]: user.title ?? '',
      [MailTemplateVars.FIRST_NAME]: user.firstName,
      [MailTemplateVars.LAST_NAME]: user.lastName,
      [MailTemplateVars.CHANGED_REGISTRATIONS]: buildRegistrationsHTML([
        {
          name: product.name,
          state: ParticipationState.left,
          topic: product.topic,
          language: product.language,
          waitingListPlace: null,
          appointments: (product as any as NarrowedEntityData<Workshop, typeof productSelectionSet>).appointments || [],
          englishVersion: false,
        },
      ]),
      [MailTemplateVars.CHANGED_REGISTRATIONS_EN]: buildRegistrationsHTML([
        {
          name: product.name,
          state: ParticipationState.left,
          topic: product.topic,
          language: product.language,
          waitingListPlace: null,
          appointments: (product as any as NarrowedEntityData<Workshop, typeof productSelectionSet>).appointments || [],
          englishVersion: true,
        },
      ]),
      [MailTemplateVars.OTHER_REGISTRATIONS]: InstitutionMailTemplate.mapOtherRegistrationsVar({
        participations: [participation],
        institution,
        products: [product],
        englishVersion: false,
      }),
      [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
      [MailTemplateVars.NEWSLETTER_OPTIN_LINK]: buildNewsletterOptinLink(token, 'de'),
      [MailTemplateVars.NEWSLETTER_OPTIN_LINK_EN]: buildNewsletterOptinLink(token, 'en'),
    };

    const { subject, content } = await assembleInstitutionMail({ template: mailTemplate, infos: mailVars as any });

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    await sendInstitutionEmail({ email: user.email, subject, content: content as TemplateResult<1>, institutionID });
  };

  const notificationType = await getNotificationType(user, mailTemplate, institutionID);
  if (notificationType === 'whatsapp') {
    if (!user.whatsappPhone) throw new Error('user has no whatsapp phone');
    await sendWhatsappWithEmailFallback({
      institutionID,
      user: { _id: user._id, whatsappPhone: user.whatsappPhone, email: user.email },
      mailTemplate,
      mailTemplateType: templateType,
      parameters: [
        InstitutionMailTemplate.mapFirstNameVar(user) || '-',
        InstitutionMailTemplate.mapLastNameVar(user) || '-',
        participation.product?.name || '-',
      ],
      sendEmail,
    });
  } else if (notificationType === 'email') {
    await sendEmail();
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}
